// @ts-check
/** @typedef {import('../types/api').InventoryVariant} InventoryVariant */
/** @typedef {import('../types/api').StockAdjustment} StockAdjustment */
/** @typedef {import('../types/api').InventoryStats} InventoryStats */
/** @typedef {import('../types/api').ProductLog} ProductLog */

import api from '../utils/apiClient';
import { assertArray, assertShape } from '../utils/apiGuards';

const adminInventoryService = {
  /**
   * @param {Object} [params]
   * @param {string} [params.search]
   * @param {number} [params.skip]
   * @param {number} [params.limit]
   * @param {string} [params.variantIds] comma-separated variant UUIDs - fetches exactly those,
   *   ignoring search/skip/limit (used by the tag-export country picker's re-fetch)
   * @param {string} [params.currency] adds display_price/formatted_price in this currency to
   *   every row (see utils/price_display on the backend)
   * @param {string} [params.country] country code for the currency conversion's tariff markup
   * @returns {Promise<{items: InventoryVariant[], total: number}>}
   */
  getAllVariants: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.skip) queryParams.append('skip', String(params.skip));
    if (params.limit) queryParams.append('limit', String(params.limit));
    if (params.variantIds) queryParams.append('variant_ids', params.variantIds);
    if (params.currency) queryParams.append('currency', params.currency);
    if (params.country) queryParams.append('country', params.country);
    const data = await api.get(`/api/admin/inventory/all-variants?${queryParams}`);
    // Backend returns {total, items} (paginated), not a bare array — asserting
    // assertArray here threw on every valid response, always surfacing as a
    // "failed to load" toast even though the request itself succeeded.
    assertShape(data, ['items', 'total'], 'adminInventoryService.getAllVariants');
    return data;
  },

  /**
   * @param {number} [threshold=2]
   * @returns {Promise<{threshold: number, count: number, items: InventoryVariant[]}>}
   */
  getLowStockProducts: async (threshold = 2) => {
    const data = await api.get(`/api/admin/inventory/low-stock?threshold=${threshold}`);
    // Backend returns {threshold, count, items}, not a bare array.
    assertShape(data, ['items'], 'adminInventoryService.getLowStockProducts');
    return data;
  },

  /**
   * @returns {Promise<{count: number, items: InventoryVariant[]}>}
   */
  getOutOfStockProducts: async () => {
    const data = await api.get('/api/admin/inventory/out-of-stock');
    // Backend returns {count, items}, not a bare array.
    assertShape(data, ['items'], 'adminInventoryService.getOutOfStockProducts');
    return data;
  },

  /**
   * @param {Object} adjustmentData
   * @returns {Promise<StockAdjustment>}
   */
  adjustStock: async (adjustmentData) => {
    return api.post('/api/admin/inventory/adjust', adjustmentData);
  },

  /**
   * Reconcile a physical count against system stock in one go - only variances get adjusted.
   * @param {{sales_event_id: string, entries: Array<{variant_id: string, counted_quantity: number}>, notes?: string | null}} data
   * @returns {Promise<{counted: number, variances: Array<{variant_id: string, old_quantity: number, new_quantity: number, adjustment: number}>}>}
   */
  recordStockCount: async (data) => {
    return api.post('/api/admin/inventory/count', data);
  },

  /**
   * @param {string} variantId
   * @returns {Promise<StockAdjustment[]>}
   */
  getStockHistory: async (variantId) => {
    const data = await api.get(`/api/admin/inventory/history/${variantId}`);
    assertArray(data, 'adminInventoryService.getStockHistory');
    return data;
  },

  /**
   * @returns {Promise<InventoryStats>}
   */
  getInventoryStats: async () => {
    const data = await api.get('/api/admin/inventory/stats');
    assertShape(data, ['total_variants'], 'adminInventoryService.getInventoryStats');
    return data;
  },

  /**
   * @param {string} productId
   * @param {number} [skip=0]
   * @param {number} [limit=50]
   * @returns {Promise<{product_name: string, logs: ProductLog[]}>}
   */
  getProductLogs: async (productId, skip = 0, limit = 50) => {
    const data = await api.get(`/api/admin/inventory/product-logs/${productId}?skip=${skip}&limit=${limit}`);
    // Backend returns {product_name, logs}, not a bare array.
    assertShape(data, ['logs'], 'adminInventoryService.getProductLogs');
    return data;
  },

  /**
   * @param {number} [limit=50]
   * @returns {Promise<ProductLog[]>}
   */
  getRecentActivity: async (limit = 50) => {
    const data = await api.get(`/api/admin/inventory/recent-activity?limit=${limit}`);
    assertArray(data, 'adminInventoryService.getRecentActivity');
    return data;
  },
};

export default adminInventoryService;
